import React from 'react';
import { cn } from '@/lib/utils';
import { ReactionStat } from '../hooks/useUserReactionStats';

interface ReactionSummaryProps {
  stats: ReactionStat[];
}

export function ReactionSummary({ stats }: ReactionSummaryProps) {
  if (stats.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <span className="text-4xl mb-3">🎭</span>
        <p className="text-foreground font-medium text-center">No reactions yet</p>
        <p className="text-muted-foreground text-sm text-center mt-1">
          Keep posting to discover your vibe!
        </p>
      </div>
    );
  }

  const totalReactions = stats.reduce((sum, s) => sum + s.count, 0);
  const topReaction = stats[0];

  return (
    <div className="p-4 space-y-6">
      {/* Top Vibe */}
      <div className="bg-primary/10 rounded-xl p-6 text-center">
        <p className="text-muted-foreground text-sm mb-2">Your Top Vibe</p>
        <span className="text-5xl block mb-2">{topReaction.emoji}</span>
        <p className="text-foreground font-bold text-lg">{topReaction.label}</p>
        <p className="text-muted-foreground text-sm">
          {topReaction.count} of {totalReactions} reactions ({Math.round((topReaction.count / totalReactions) * 100)}%)
        </p>
      </div>

      {/* All Reactions Breakdown */}
      <div>
        <h3 className="text-foreground font-semibold mb-3">Reaction Breakdown</h3>
        <div className="space-y-3">
          {stats.map((stat) => {
            const percentage = Math.round((stat.count / totalReactions) * 100);
            
            return (
              <div key={stat.reaction_type} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{stat.emoji}</span>
                    <span className="text-foreground">{stat.label}</span>
                  </div>
                  <span className="text-muted-foreground">{stat.count}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all",
                      stat === topReaction ? "bg-primary" : "bg-primary/50"
                    )}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Total */}
      <div className="text-center pt-4 border-t border-border">
        <p className="text-muted-foreground text-sm">
          Total reactions received: <span className="text-foreground font-bold">{totalReactions}</span>
        </p>
      </div>
    </div>
  );
}
